
import React from 'react';
import { NavLink } from '../navigation/NavLink';
import { NotificationCenter } from '../NotificationCenter';
import { ChevronRight, Home, Search, Map, FolderHeart, FileText, User, Calendar, Briefcase, Bell } from 'lucide-react';

interface SimpleMobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  isAuthenticated: boolean;
  isHost: boolean;
  onLogout: () => void;
}

const MenuItem = ({ to, icon: Icon, children, onClick }: {
  to: string;
  icon: React.ElementType;
  children: React.ReactNode;
  onClick?: () => void;
}) => (
  <NavLink 
    to={to} 
    className="group flex items-center justify-between p-4 rounded-xl bg-white shadow-sm border border-gray-100 hover:border-primary/20 hover:bg-primary/5 transition-all duration-200 touch-manipulation"
    onClick={onClick}
  >
    <div className="flex items-center gap-3">
      <Icon className="h-5 w-5 text-primary" />
      <span className="font-medium text-gray-800">{children}</span>
    </div>
    <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-primary transition-colors" />
  </NavLink>
);

export const SimpleMobileMenu: React.FC<SimpleMobileMenuProps> = ({
  isOpen,
  onClose,
  isAuthenticated,
  isHost,
  onLogout
}) => {
  if (!isOpen) return null;

  return (
    <div className="md:hidden fixed inset-0 z-50 bg-white">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-white">
        <NavLink to="/" onClick={onClose}>
          <span className="text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            <span className="t-logo">t</span>ravilis
          </span>
        </NavLink>
        
        <div className="flex items-center gap-3">
          {isAuthenticated && (
            <div className="p-2">
              <NotificationCenter />
            </div>
          )}
          <button 
            onClick={onClose} 
            className="p-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors touch-manipulation"
          >
            <span className="sr-only">Close menu</span>
            ✕
          </button>
        </div>
      </div>
      
      {/* Menu Content */}
      <div className="p-4 space-y-6 overflow-y-auto h-full pb-20">
        {/* Main Navigation */}
        <div className="space-y-3">
          <h2 className="text-sm font-medium text-gray-500 px-2">Explore</h2>
          <MenuItem to="/" icon={Home} onClick={onClose}>Home</MenuItem>
          <MenuItem to="/search" icon={Search} onClick={onClose}>Explore Destinations</MenuItem>
          <MenuItem to="/recommendations" icon={Map} onClick={onClose}>Recommendations</MenuItem>
          <MenuItem to="/visa-requirements" icon={FileText} onClick={onClose}>Visa Requirements</MenuItem>
          <MenuItem to="/collections" icon={FolderHeart} onClick={onClose}>Collections</MenuItem>
        </div>
        
        {/* User Menu */}
        {isAuthenticated && (
          <div className="space-y-3">
            <h2 className="text-sm font-medium text-gray-500 px-2">Your Account</h2>
            <MenuItem to="/profile" icon={User} onClick={onClose}>Profile</MenuItem>
            <MenuItem to="/trips" icon={Briefcase} onClick={onClose}>My Trips</MenuItem>
            {isHost && (
              <MenuItem to="/availability" icon={Calendar} onClick={onClose}>Manage Availability</MenuItem>
            )}
          </div>
        )}
        
        {/* Auth Button */}
        <div className="fixed bottom-6 left-4 right-4">
          <button 
            className="w-full bg-primary text-white py-3 rounded-lg font-medium text-lg shadow-lg hover:bg-primary/90 active:scale-95 transition-all touch-manipulation"
            onClick={() => {
              onClose();
              if (isAuthenticated) {
                onLogout();
              } else {
                window.location.href = '/auth';
              }
            }}
          >
            {isAuthenticated ? 'Sign Out' : 'Sign In'}
          </button>
        </div>
      </div>
    </div>
  );
};
